import React, { Suspense, lazy, useEffect, useState, useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Card, Skeleton, Snackbar, Alert, Button as MuiButton } from '@mui/material';
import { Lock } from '@mui/icons-material';
import { FuelMonitoringThemeProvider } from './theme/ThemeProvider';
import { Layout } from './components/layout';
import { SiteData, AlertData } from './utils/mockData';
import LoginPage from './components/auth/LoginPage';
import { tokenStorage, logoutUser, AlertRecord, deleteAlert, fetchSites, fetchSiteGenerators, fetchSiteTanks, fetchPollAlerts, fetchPollLiveData, fetchAnalyticsSummary } from './utils/auth';
import { canAccessPath, PermissionsContext } from './utils/permissions';
import { useIdleLogout, resetIdleClock, clearIdleClock } from './hooks/useIdleLogout';
import SignupPage from './components/auth/SignupPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import Presentation from './components/presentation/Presentation';

// Lazy load components to reduce initial bundle size
const OverviewTab       = lazy(() => import('./components/dashboard/tabs/OverviewTab'));
const AnalyticsTab      = lazy(() => import('./components/dashboard/tabs/AnalyticsTab'));
const QualityTab        = lazy(() => import('./components/dashboard/tabs/QualityTab'));
const SecurityTab       = lazy(() => import('./components/dashboard/tabs/SecurityTab'));
const MapTab            = lazy(() => import('./components/dashboard/tabs/MapTab'));
const DevicesTab        = lazy(() => import('./components/dashboard/tabs/DevicesTab'));
const SettingsTab       = lazy(() => import('./components/dashboard/tabs/SettingsTab'));
const SiteAssetsTab     = lazy(() => import('./components/dashboard/tabs/SiteAssetsTab'));
const UsersSuppliersTab = lazy(() => import('./components/dashboard/tabs/UsersSuppliersTab'));
const TankersTab        = lazy(() => import('./components/dashboard/tabs/TankersTab'));
const DriversTab        = lazy(() => import('./components/dashboard/tabs/DriversTab'));
const ReportTab         = lazy(() => import('./components/dashboard/tabs/ReportTab'));
const LogsTab           = lazy(() => import('./components/dashboard/tabs/LogsTab'));
const FuelTab           = lazy(() => import('./components/dashboard/tabs/FuelTab'));
const SapTab            = lazy(() => import('./components/dashboard/tabs/SapTab'));
const ProfilePage       = lazy(() => import('./components/dashboard/tabs/ProfilePage'));
const NotificationsPage = lazy(() => import('./components/dashboard/tabs/NotificationsPage'));
const AlertsPage        = lazy(() => import('./components/dashboard/tabs/AlertsPage'));

interface UserData {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'Admin' | 'Manager' | 'Operator' | 'Auditor';
  avatar?: string;
  permissions: string[];
  isOwner: boolean;
  sites: string[];
}

const TAB_TO_PATH: Record<number, string> = {
  0: '/dashboard',
  1: '/analytics',
  2: '/monitoring/fuel',
  3: '/security',
  4: '/sites/map',
  5: '/devices',
  6: '/settings',
  7: '/sites/infrastructure',
  8: '/users',
  9: '/sites/tankers',
  10: '/sites/drivers',
  11: '/reports',
  12: '/logs',
  13: '/fuel',
  14: '/sap',
};

const PATH_TO_TAB: Record<string, number> = Object.fromEntries(
  Object.entries(TAB_TO_PATH).map(([k, v]) => [v, Number(k)])
);

// Pages outside the tab switch.
const STANDALONE_PATHS = new Set(['/profile', '/notifications', '/alerts']);

/* Shown when a signed-in user opens a route their permissions don't cover —
   a typed URL, an old bookmark, or a role change mid-session. The sidebar
   already hides these, so this is the backstop, not the usual path. */
const AccessDenied: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', textAlign: 'center', py: 10, px: 3,
  }}>
    <Box sx={{
      width: 56, height: 56, borderRadius: '50%', mb: 2.5,
      bgcolor: 'rgba(43,103,176,0.08)', border: '1px solid rgba(43,103,176,0.2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Lock sx={{ fontSize: 26, color: 'rgba(43,103,176,1)' }} />
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827', mb: 0.75 }}>
      You don't have access to this page
    </Typography>
    <Typography sx={{ fontSize: '0.86rem', color: '#6B7280', maxWidth: 420, mb: 2.5 }}>
      Your account's permissions don't cover this section. If you need it, ask an
      administrator to update your role.
    </Typography>
    <MuiButton variant="contained" onClick={onBack}
      sx={{
        textTransform: 'none', borderRadius: '10px', fontWeight: 600,
        fontSize: '0.85rem', px: 2.5,
        bgcolor: 'rgba(43,103,176,1)', '&:hover': { bgcolor: 'rgba(33,83,146,1)' },
      }}
    >
      Back to dashboard
    </MuiButton>
  </Box>
);

function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sites, setSites] = useState<SiteData[]>([]);
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const activeTab = PATH_TO_TAB[location.pathname] ?? 0;
  const [user, setUser] = useState<UserData | null>(() => {
    try {
      const saved = localStorage.getItem('fuelMonitoringAuth');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [loginToast, setLoginToast] = useState(false);
  const [idleNotice, setIdleNotice] = useState(false);

  const handleLogin = (userData: UserData) => {
    resetIdleClock();
    setIdleNotice(false);
    setUser(userData);
    localStorage.setItem('fuelMonitoringAuth', JSON.stringify(userData));
    setLoginToast(true);
    // A logged-out user who followed a link (e.g. an alert email to /alerts) signed in on
    // that same URL; send them back to it rather than to the dashboard.
    const target = location.pathname;
    const deepLink = target !== '/' && target !== '/dashboard' &&
      (PATH_TO_TAB[target] !== undefined || STANDALONE_PATHS.has(target));
    navigate(deepLink ? `${target}${location.search}` : '/dashboard');
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('fuelMonitoringAuth');
    clearIdleClock();
    logoutUser();
    navigate('/');
  };

  // 60 minutes without any input signs the user out and returns them to the login page.
  useIdleLogout({
    enabled: !!user,
    onIdle: () => { setIdleNotice(true); handleLogout(); },
  });

  // Dropped here when a background API call's silent token refresh fails —
  // the refresh token itself has expired/been revoked, so the session is over.
  useEffect(() => {
    const handler = () => handleLogout();
    window.addEventListener('pt360:session-expired', handler);
    return () => window.removeEventListener('pt360:session-expired', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sites — initial load from /api/v1/sites/, then live-update via poll every 15s
  useEffect(() => {
    if (!user) return;

    let mounted = true;

    // Initial load: fetch site list, then enrich each site with tanks + generators in parallel.
    // The site list endpoint does not embed either; they live at separate endpoints.
    fetchSites({ limit: 100 })
      .then(async ({ results }) => {
        if (!mounted || results.length === 0) return;
        setSites(results); // render sites immediately while enrichment is in flight
        const enriched = await Promise.all(
          results.map(async (site) => {
            const [tanks, generators] = await Promise.all([
              fetchSiteTanks(site.id).catch(() => site.tanks),
              fetchSiteGenerators(site.id).catch(() => site.generators),
            ]);
            return { ...site, tanks, generators };
          })
        );
        if (mounted) setSites(enriched);
      })
      .catch(() => {});

    // Ongoing live updates — preserve enriched tanks/generators when the poll omits them.
    const poll = async () => {
      try {
        const { sites: polled } = await fetchPollLiveData();
        if (!mounted || polled.length === 0) return;
        setSites(prev => {
          const prevById = Object.fromEntries(prev.map(s => [s.id, s]));
          return polled.map(site => ({
            ...site,
            tanks: site.tanks.length > 0
              ? site.tanks
              : (prevById[site.id]?.tanks ?? []),
            generators: site.generators.length > 0
              ? site.generators
              : (prevById[site.id]?.generators ?? []),
          }));
        });
      } catch {
        // endpoint unavailable — silently retry next cycle
      }
    };

    const interval = setInterval(poll, 15000);

    return () => { mounted = false; clearInterval(interval); };
  }, [user]);

  // Alerts — poll every 10s; pass `since` so each cycle only fetches new alerts
  useEffect(() => {
    if (!user) return;

    let mounted = true;
    let lastTimestamp: string | undefined;

    const poll = async () => {
      try {
        const { results, newAlerts, timestamp } = await fetchPollAlerts({ since: lastTimestamp });
        if (!mounted) return;
        if (!lastTimestamp) {
          // First call — seed with the full current list.
          setAlerts(results as AlertRecord[] as AlertData[]);
        } else if (newAlerts.length > 0) {
          // Subsequent calls — the server already tells us exactly what's new
          // since `since`, so no client-side dedup is needed.
          setAlerts(prev => [...(newAlerts as AlertRecord[] as AlertData[]), ...prev]);
        }
        lastTimestamp = timestamp;
      } catch {
        // endpoint unavailable — silently retry next cycle
      }
    };

    poll();
    const interval = setInterval(poll, 10000);
    return () => { mounted = false; clearInterval(interval); };
  }, [user]);

  // Manual "refresh everything now" — used by the Management Center's Clear
  // Cache action, since this app has no actual cache layer; the closest
  // correct behavior is discarding local state and pulling fresh from the
  // server, same shape as the initial load above.
  const handleManualRefresh = useCallback(async () => {
    const [{ results: freshSites }, { results: freshAlerts }] = await Promise.all([
      fetchSites({ limit: 100 }),
      fetchPollAlerts({}),
    ]);
    setAlerts(freshAlerts as AlertRecord[] as AlertData[]);
    if (freshSites.length === 0) { setSites([]); return; }
    setSites(freshSites);
    const enriched = await Promise.all(
      freshSites.map(async (site) => {
        const [tanks, generators] = await Promise.all([
          fetchSiteTanks(site.id).catch(() => site.tanks),
          fetchSiteGenerators(site.id).catch(() => site.generators),
        ]);
        return { ...site, tanks, generators };
      })
    );
    setSites(enriched);
  }, []);

  const [quickStats, setQuickStats] = useState({
    activeSites: 0, runningGenerators: 0, activeAlerts: 0,
  });

  // Sync quick stats: use API summary for accurate generator count, fall back to local state
  useEffect(() => {
    if (!user) return;
    let mounted = true;

    const refresh = async () => {
      try {
        const summary = await fetchAnalyticsSummary();
        if (!mounted) return;
        setQuickStats({
          activeSites:       summary.totalSites,
          runningGenerators: summary.runningGenerators,
          activeAlerts:      summary.activeAlerts,
        });
      } catch {
        // fall back to locally derived values if API unavailable
        if (mounted) setQuickStats({
          activeSites:       sites.length,
          runningGenerators: sites.reduce((n, s) => n + s.generators.filter(g => g.status === 'running').length, 0),
          activeAlerts:      alerts.filter(a => !a.resolved).length,
        });
      }
    };

    refresh();
    const interval = setInterval(refresh, 15000);
    return () => { mounted = false; clearInterval(interval); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Memoize notifications to prevent unnecessary re-renders
  const notifications = useMemo(() => ({
    count: alerts.filter(alert => !alert.acknowledged).length,
    items: alerts.slice(0, 3).map(alert => ({ // Reduced to 3 items
      id: alert.id,
      title: alert.title,
      message: alert.message,
      time: new Date(alert.timestamp).toLocaleString(),
      severity: alert.severity === 'critical' ? 'error' as const :
                alert.severity === 'high' || alert.severity === 'medium' ? 'warning' as const :
                'info' as const,
    })),
  }), [alerts]);


  const handleSidebarNavigation = (path: string) => {
    const known = PATH_TO_TAB[path] !== undefined || STANDALONE_PATHS.has(path);
    if (known && canAccessPath(user, path)) navigate(path);
  };

  // Only real destinations are gated. An unrecognised path (including "/") still
  // falls through to the Overview tab as before — that's a wrong-URL case, not a
  // permissions one, and shouldn't accuse the user of lacking access.
  const isKnownPath =
    PATH_TO_TAB[location.pathname] !== undefined || STANDALONE_PATHS.has(location.pathname);
  const pathAllowed = !isKnownPath || canAccessPath(user, location.pathname);

  const LoadingFallback = () => (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <CircularProgress
            size={24}
            sx={{ color: 'rgba(43, 103, 176, 1)' }}
          />
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            Loading dashboard...
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {[...Array(6)].map((_, index) => (
            <Card key={index}>
              <Box sx={{ p: 2 }}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mb: 1 }} />
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="80%" height={20} />
              </Box>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );

  if (!user) {
    return (
      <FuelMonitoringThemeProvider>
        <LoginPage onLogin={handleLogin} />
        <Snackbar open={idleNotice} onClose={() => setIdleNotice(false)} autoHideDuration={10000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setIdleNotice(false)} severity="info" variant="filled" sx={{ borderRadius: '10px' }}>
            You were signed out after 60 minutes of inactivity. Please sign in again.
          </Alert>
        </Snackbar>
      </FuelMonitoringThemeProvider>
    );
  }

  return (
    <FuelMonitoringThemeProvider>
      <Snackbar
        open={loginToast}
        autoHideDuration={3000}
        onClose={() => setLoginToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setLoginToast(false)}
          severity="success"
          variant="filled"
          sx={{ borderRadius: '10px', fontFamily: '"Montserrat", sans-serif', fontSize: '0.85rem' }}
        >
          Welcome back, {user.name}!
        </Alert>
      </Snackbar>
      <PermissionsContext.Provider value={user}>
      <Layout
        currentPath={location.pathname}
        user={user}
        notifications={notifications}
        onNavigate={handleSidebarNavigation}
        onLogout={handleLogout}
        quickStats={quickStats}
        access={user}
      >
        <Box>
          <Suspense fallback={<LoadingFallback />}>
          {!pathAllowed ? (
            <AccessDenied onBack={() => navigate('/dashboard')} />
          ) : (
            <>
            {/* Standalone pages — no tab bar */}
            {location.pathname === '/profile' && (
              <ProfilePage user={user} onLogout={handleLogout} />
            )}
            {location.pathname === '/alerts' && (
              <AlertsPage
                sites={sites}
                onAlertChanged={(id, patch) => setAlerts(prev => prev.map(a => a.id === id ? { ...a, ...patch } as AlertData : a))}
              />
            )}
            {location.pathname === '/notifications' && (
              <NotificationsPage
                alerts={alerts}
                onMarkAsRead={(id) => setAlerts(prev => prev.map(a => a.id === id ? { ...a, acknowledged: true } : a))}
                onDeleteAlert={(id) => setAlerts(prev => prev.filter(a => a.id !== id))}
                onMarkAllAsRead={() => setAlerts(prev => prev.map(a => ({ ...a, acknowledged: true })))}
                onAlertResolved={(id) => setAlerts(prev => prev.map(a => a.id === id ? { ...a, resolved: true, acknowledged: true } : a))}
              />
            )}

            {/* Tab-based pages — navigation lives entirely in the sidebar now */}
            {!STANDALONE_PATHS.has(location.pathname) && (
              <>
                {activeTab === 0 && <OverviewTab sites={sites} alerts={alerts} userRole={user.role} onRefreshData={handleManualRefresh} />}
                {activeTab === 1 && <AnalyticsTab sites={sites} userRole={user.role} />}
                {activeTab === 2 && <QualityTab sites={sites} alerts={alerts} />}
                {activeTab === 3 && <SecurityTab sites={sites} userRole={user.role} />}
                {activeTab === 4 && <MapTab sites={sites} alerts={alerts} />}
                {activeTab === 5 && <DevicesTab sites={sites} />}
                {activeTab === 6 && <SettingsTab />}
                {activeTab === 7 && <SiteAssetsTab sites={sites}
                  onSiteCreated={(savedSite) => setSites(prev => {
                    const idx = prev.findIndex(s => s.id === savedSite.id);
                    if (idx >= 0) {
                      // The site update endpoint only echoes back name/location — it
                      // doesn't nest tanks/generators the way the list endpoint does.
                      // Merge just the edited fields so an existing site's tanks and
                      // generators aren't wiped out until the next refresh.
                      const next = [...prev];
                      next[idx] = {
                        ...next[idx],
                        name:     savedSite.name,
                        location: savedSite.location,
                      };
                      return next;
                    }
                    return [...prev, savedSite];
                  })}
                  onSiteDeleted={(id) => setSites(prev => prev.filter(s => s.id !== id))}
                  onTankCreated={(siteId, tank) => setSites(prev => prev.map(s =>
                    s.id === siteId ? { ...s, tanks: [...s.tanks, tank] } : s
                  ))}
                  onGeneratorCreated={(siteId, generator) => setSites(prev => prev.map(s =>
                    s.id === siteId ? { ...s, generators: [...s.generators, generator] } : s
                  ))}
                />}
                {activeTab === 8 && <UsersSuppliersTab />}
                {activeTab === 9 && <TankersTab sites={sites} />}
                {activeTab === 10 && <DriversTab sites={sites} />}
                {activeTab === 11 && <ReportTab sites={sites} alerts={alerts} userRole={user.role} />}
                {activeTab === 12 && <LogsTab sites={sites} />}
                {activeTab === 13 && <FuelTab sites={sites} />}
                {activeTab === 14 && <SapTab  sites={sites} />}
              </>
            )}
            </>
          )}
          </Suspense>
        </Box>
      </Layout>
      </PermissionsContext.Provider>
    </FuelMonitoringThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/presentation" element={<Presentation />} />
        <Route path="/signup" element={<FuelMonitoringThemeProvider><SignupPage /></FuelMonitoringThemeProvider>} />
        <Route path="/forgot-password" element={<FuelMonitoringThemeProvider><ForgotPasswordPage /></FuelMonitoringThemeProvider>} />
        <Route path="/reset-password" element={<FuelMonitoringThemeProvider><ResetPasswordPage /></FuelMonitoringThemeProvider>} />
        <Route path="/*" element={<Dashboard />} />
      </Routes>
    </Router>
  );
}

export default App;
